import { useEffect, useState } from "react";
import { disableScheduledJob, enableScheduledJob, listScheduledJobs } from "../api/ops.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

interface ScheduledJob {
  id: string;
  name: string;
  agentName: string;
  jobType: string;
  cronExpression: string | null;
  status: string;
  enabled: boolean;
  nextRunAt: string | null;
  lastRunAt: string | null;
  version: number;
  priority: string;
}

export function OpsSchedulerPage() {
  const { token } = useAuth();
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    void listScheduledJobs(token).then((d: unknown) => {
      setJobs((d as { items: ScheduledJob[] }).items ?? []);
      setLoading(false);
    });
  };

  useEffect(() => {
    reload();
  }, []);

  const toggle = async (job: ScheduledJob) => {
    if (job.enabled) {
      await disableScheduledJob(token, job.id, job.version);
    } else {
      await enableScheduledJob(token, job.id, job.version);
    }
    reload();
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <h1 className="mb-6 text-2xl font-bold text-white">Scheduled Jobs</h1>
        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : (
          <div className="space-y-3">
            {jobs.length === 0 && <p className="text-gray-400">No scheduled jobs.</p>}
            {jobs.map((job) => (
              <div key={job.id} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                <div className="mb-2 flex items-center justify-between">
                  <div>
                    <span className="font-medium text-white">{job.name}</span>
                    <span className="ml-2 text-xs text-gray-500">{job.agentName}</span>
                    <span className="ml-2 rounded bg-gray-800 px-1.5 py-0.5 text-xs text-gray-400">
                      {job.jobType}
                    </span>
                    {job.cronExpression && (
                      <span className="ml-2 font-mono text-xs text-indigo-400">
                        {job.cronExpression}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => void toggle(job)}
                    className={`rounded px-3 py-1 text-xs ${job.enabled ? "bg-red-900/50 text-red-300 hover:bg-red-900" : "bg-green-900/50 text-green-300 hover:bg-green-900"}`}
                  >
                    {job.enabled ? "Disable" : "Enable"}
                  </button>
                </div>
                <div className="flex gap-4 text-xs text-gray-500">
                  {job.nextRunAt && <span>Next: {new Date(job.nextRunAt).toLocaleString()}</span>}
                  {job.lastRunAt && <span>Last: {new Date(job.lastRunAt).toLocaleString()}</span>}
                  <span className={job.enabled ? "text-green-400" : "text-gray-600"}>
                    {job.enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
